import React from "react";
import { Box, Typography, useTheme } from "@mui/material";

const stripHtml = (html) =>
  typeof html === "string" ? html.replace(/<[^>]*>/g, "").trim() : "";

/** Evita exibir lixo de datas inválidas/corrompidas em logs já persistidos. */
const sanitizeLogLabel = (label) => {
  if (label == null) return label;
  const t = String(label).trim();
  if (
    t === "" ||
    /^undefined\b/i.test(t) ||
    t === "Invalid Date" ||
    /^null$/i.test(t)
  ) {
    return "-";
  }
  return label;
};

const parseStructuredLog = (text) => {
  if (!text) {
    return { prefix: "", from: null, to: null };
  }

  const original = text.trim();
  let prefix = original;
  let from = null;
  let to = null;

  // Suporta formatos:
  // - "... De X para Y"
  // - "... de X para Y"
  // - "... do X para o Y"
  // - "... da X para a Y"
  // sem perder o conectivo "para" na renderização.
  const match = original.match(
    /^(.*?)(?:\s+(?:de|do|da))\s+(.+?)\s+para(?:\s+(?:o|a))?\s+(.+?)\.?$/i
  );

  if (match) {
    prefix = (match[1] || "").trim();
    from = sanitizeLogLabel((match[2] || "").trim().replace(/\.$/, ""));
    to = sanitizeLogLabel((match[3] || "").trim().replace(/\.$/, ""));
  } else {
    const idx = original.toLowerCase().indexOf(" para ");
    if (idx !== -1) {
      prefix = original.slice(0, idx).trim();
      to = sanitizeLogLabel(
        original
          .slice(idx + 6)
          .trim()
          .replace(/\.$/, "")
          // Remove artigos iniciais para ficar bonito no pill (ex.: "o atendente X", "a fila")
          .replace(/^(o|a|os|as)\s+/i, "")
      );
    }
  }

  return { prefix, from, to };
};

const STATUS_COLOR_KEYS = {
  Pendente: "pending",
  "Em Atendimento": "inProgress",
  Concluída: "success",
  "Contato Futuro": "future",
  Perdida: "unsuccessful",
  Cancelada: "canceled",
};

const isStatusLabel = (label) =>
  !!label && Object.prototype.hasOwnProperty.call(STATUS_COLOR_KEYS, label);

const StatusPill = ({ label, theme }) => {
  if (!label) return null;

  if (!isStatusLabel(label)) {
    return (
      <Box
        component="span"
        sx={{
          color: theme.palette.primary.main,
          fontWeight: 700,
        }}
      >
        {label}
      </Box>
    );
  }

  const colorKey = STATUS_COLOR_KEYS[label];
  const bg = theme.palette.status?.[colorKey] || theme.palette.primary.light;
  const isLightLabel =
    label === "Pendente" ||
    label === "Em Atendimento" ||
    label === "Contato Futuro";
  const textColor = isLightLabel
    ? theme.palette.text.primary
    : theme.palette.common.white;

  return (
    <Box
      component="span"
      sx={{
        px: 1,
        py: 0.2,
        borderRadius: 999,
        backgroundColor: bg,
        color: textColor,
        fontWeight: 700,
        fontSize: "0.85em",
      }}
    >
      {label}
    </Box>
  );
};

const LogMessage = ({ message }) => {
  const theme = useTheme();
  const plain = stripHtml(message?.text || "");
  const { prefix, from, to } = parseStructuredLog(plain);
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        textAlign: "center",
        color: textPrimary,
      }}
    >
      <Typography
        component="div"
        sx={{
          fontSize: "0.875rem",
          lineHeight: 1.4,
          color: "inherit",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {prefix && (
          <Box
            component="span"
            sx={{
              color: textPrimary,
              fontWeight: 600,
              mr: from || to ? 0.5 : 0,
            }}
          >
            {prefix}
          </Box>
        )}

        {from && (
          <>
            <Box
              component="span"
              sx={{ color: textSecondary, mr: 0.5, fontWeight: 500 }}
            >
              de
            </Box>
            <Box component="span" sx={{ mr: 0.5 }}>
              <StatusPill label={from} theme={theme} />
            </Box>
            <Box
              component="span"
              sx={{ color: textSecondary, mr: 0.5, fontWeight: 500 }}
            >
              para
            </Box>
          </>
        )}

        {to && !from && (
          <Box
            component="span"
            sx={{ color: textSecondary, mr: 0.5, fontWeight: 500, display: "inline-flex" }}
          >
            para
          </Box>
        )}
        {to && <StatusPill label={to} theme={theme} />}

        {!from && !to && !prefix && (
          <Box
            component="span"
            sx={{
              color: textPrimary,
              fontWeight: 600,
            }}
          >
            {plain}
          </Box>
        )}
      </Typography>
    </Box>
  );
};

export default LogMessage;
